// src/components/headerone/SharedHeader.js
// Shared header component with Cognito authentication
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { useNavigation } from '../../routes/NavigationContext';
import Logo from '../../assets/image/StateofGTSLogo.png';
import CustomLink from '../../routes/link.js';
import { useAuth } from '../../routes/AuthContext.js';
import cognitoConfig from '../loginAWS/cognitoConfig.json';

// ----- bridge identity + context info to Amazon Connect -----
function ConnectAttributes({ payload }) {
  const location = useLocation();

  useEffect(() => {
    const firstName = payload?.given_name || '';
    const lastName = payload?.family_name || '';
    const fullName =
      firstName || lastName
        ? `${firstName} ${lastName}`.trim()
        : payload?.['cognito:username'] || 'Guest';

    const customerID = payload?.['custom:User_ID'] || '';

    const getBrowserDetails = () => {
      const ua = navigator.userAgent || '';
      const mCriOS = ua.match(/CriOS\/([\d.]+)/); if (mCriOS) return `Chrome iOS ${mCriOS[1]}`;
      const mFxiOS = ua.match(/FxiOS\/([\d.]+)/); if (mFxiOS) return `Firefox iOS ${mFxiOS[1]}`;
      const mEdgiOS = ua.match(/EdgiOS\/([\d.]+)/); if (mEdgiOS) return `Edge iOS ${mEdgiOS[1]}`;
      const mOPiOS = ua.match(/OPiOS\/([\d.]+)/); if (mOPiOS) return `Opera iOS ${mOPiOS[1]}`;
      const mEdge = ua.match(/Edg\/([\d.]+)/);     if (mEdge)  return `Edge ${mEdge[1]}`;
      const mOPR = ua.match(/OPR\/([\d.]+)/);     if (mOPR)   return `Opera ${mOPR[1]}`;
      const mFirefox = ua.match(/Firefox\/([\d.]+)/); if (mFirefox) return `Firefox ${mFirefox[1]}`;
      const mChrome = /Chrome\/([\d.]+)/.exec(ua); if (mChrome && !/Chromium|OPR|Edg/.test(ua)) return `Chrome ${mChrome[1]}`;
      const mSafari = /Safari\/[\d.]+/.test(ua) && ua.match(/Version\/([\d.]+)/); if (mSafari) return `Safari ${mSafari[1]}`;
      const mChromium = ua.match(/Chromium\/([\d.]+)/); if (mChromium) return `Chromium ${mChromium[1]}`;
      const brand = navigator.userAgentData?.brands?.find(b => b.brand && b.brand !== 'Chromium')
        || navigator.userAgentData?.brands?.[0];
      if (brand) return `${brand.brand} ${brand.version}`;
      return 'Unknown';
    };

    const getOS = () => {
      const ua = navigator.userAgent || '';
      if (/Windows NT/i.test(ua)) return 'Windows';
      if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
      if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
      if (/Android/i.test(ua)) return 'Android';
      if (/CrOS/i.test(ua)) return 'Chrome OS';
      if (/Linux/i.test(ua)) return 'Linux';
      return navigator.platform || 'Unknown';
    };

    const getScreenResolution = () =>
      `${window.screen?.width || 0}x${window.screen?.height || 0}`;

    const attrs = {
      subjectLine: document.title,
      currentURL: window.location.href,
      browserDetails: getBrowserDetails(),
      screenResolution: getScreenResolution(),
      platform: getOS(),
      customerID,
      customerName: fullName
    };

    // Include SMS opt-in if available
    if (payload?.['custom:sms_optin']) {
      attrs['sms_optin'] = payload['custom:sms_optin'];
    }

    window.__gtsGetConnectAttributes = () => attrs;

    try {
      sessionStorage.setItem(
        'gts.connect.contactAttributes',
        JSON.stringify(attrs)
      );
    } catch {}

    try {
      window.dispatchEvent(
        new CustomEvent('gts:connect:attributes', { detail: attrs })
      );
    } catch {}

    try {
      if (window.connect?.ChatInterface?.updateContactAttributes) {
        window.connect.ChatInterface.updateContactAttributes({
          attributes: attrs
        });
      }
    } catch {}
  }, [payload, location.pathname, location.search]);

  return null;
}

// ----- Cognito pool for logout -----
const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.UserPoolId,
  ClientId: cognitoConfig.ClientId
});

// Helper to extract user data from Cognito payload
export const extractUserDataFromPayload = (payload) => {
  if (!payload) return null;

  const firstName = payload.given_name || '';
  const lastName = payload.family_name || '';
  const fullName =
    firstName || lastName
      ? `${firstName} ${lastName}`.trim()
      : (payload['cognito:username'] || '');
  
  const userId = payload['custom:User_ID'] || '';
  // Extract last 4 digits for customerID (matching the format used by other bots)
  const customerID = userId.length >= 4 ? userId.slice(-4) : userId;
  
  // Get email if available
  const emailAddress = payload.email || payload['cognito:username'] || '';
  
  // For account number, use the full User_ID (8x8 expects this)
  const accountNumber = userId || '';

  return {
    firstName,
    lastName,
    fullName,
    userId,
    customerID,
    emailAddress,
    accountNumber,
    payload
  };
};

function SharedHeader({ botComponent: BotComponent, activeBotId: activeBotIdProp }) {
  const { currentPath } = useNavigation();
  const navigate = useNavigate();

  const [Btnshow, setBtnshow] = useState(false);
  const [search, setsearch] = useState(false);
  const [Home, setHome] = useState(false);
  const [Pages, setPages] = useState(false);
  const [Services, setServices] = useState(false);
  const [News, setNews] = useState(false);
  const [Departments, setDepartments] = useState(false);
  const [Events, setEvents] = useState(false);
  const [botKey, setBotKey] = useState(0);
  const [activeBotId, setActiveBotId] = useState(activeBotIdProp || 'omni'); // Use prop or default to omni
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [showSnapSubmenu, setShowSnapSubmenu] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null); // Track which submenu is active: 'dmv', 'health', 'education', etc.
  const [departmentsDropdownOpen, setDepartmentsDropdownOpen] = useState(false); // Track if departments dropdown should stay open
  const departmentsCloseTimeoutRef = useRef(null); // Ref to track close timeout
  const submenuClickTimeRef = useRef(null); // Track when a submenu item was last clicked

  const { authUser, setAuthUser } = useAuth();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (departmentsCloseTimeoutRef.current) {
        clearTimeout(departmentsCloseTimeoutRef.current);
      }
    };
  }, []);

  // Update activeBotId when prop changes
  useEffect(() => {
    if (activeBotIdProp && activeBotIdProp !== activeBotId) {
      setActiveBotId(activeBotIdProp);
    }
  }, [activeBotIdProp, activeBotId]);

  // Fallback: Get active bot ID from localStorage if prop not provided
  useEffect(() => {
    if (!activeBotIdProp) {
      const getActiveBotId = () => {
        try {
          const stored = localStorage.getItem('gts.header.variant');
          if (stored && ['aws', 'genesys', 'omni', 'omni8x8', '8x8', 'omni2', 'omni2demo'].includes(stored)) {
            return stored;
          }
        } catch {}
        return 'omni'; // Default
      };
      
      setActiveBotId(getActiveBotId());
      
      // Listen for changes to the bot variant
      const handleStorageChange = (e) => {
        if (e.key === 'gts.header.variant') {
          setActiveBotId(getActiveBotId());
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      
      // Also check periodically in case localStorage is updated in same window
      const interval = setInterval(() => {
        const current = getActiveBotId();
        if (current !== activeBotId) {
          setActiveBotId(current);
        }
      }, 500);
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        clearInterval(interval);
      };
    }
  }, [activeBotIdProp, activeBotId]);

  // Get phone number based on active bot
  const getPhoneNumber = () => {
    switch (activeBotId) {
      case '8x8':
        return { display: '(423) 466-4440', href: 'tel:+14234664440' };
      case 'aws':
        return { display: '(920) 945-6191', href: 'tel:+19209456191' };
      case 'genesys':
      case 'omni':
      case 'omni8x8':
      default:
        // Default phone number (from Footer - current number for Genesys and Omni)
        return { display: '(423) 834-7642', href: 'tel:+14238347642' };
    }
  };

  const phoneInfo = getPhoneNumber();
  
  // Extract payload from authUser - handle both session object and stored format
  let payload = null;
  
  // First try to get payload from localStorage (most reliable)
  try {
    const stored = localStorage.getItem('authUser');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.payload) {
        payload = parsed.payload;
      }
    }
  } catch {}
  
  // If not in localStorage, try from authUser session object
  if (!payload && authUser) {
    if (authUser.idToken) {
      // If idToken is a CognitoIdToken object, try to get payload
      if (typeof authUser.idToken.getPayload === 'function') {
        try {
          payload = authUser.idToken.getPayload();
        } catch {}
      } else if (authUser.idToken.payload) {
        payload = authUser.idToken.payload;
      } else if (typeof authUser.idToken.decodePayload === 'function') {
        try {
          payload = authUser.idToken.decodePayload();
        } catch {}
      }
    } else if (authUser.payload) {
      // Fallback: payload might be directly on authUser
      payload = authUser.payload;
    }
  }

  const userData = extractUserDataFromPayload(payload);

  // REMOVED: botKey increment on route changes
  // This was causing the bot component to potentially remount unnecessarily
  // The bot should persist across route changes without remounting
  // useEffect(() => {
  //   setBotKey((prevKey) => prevKey + 1);
  // }, [currentPath]);

  const handleLogout = () => {
    try {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
    } catch (e) {
      console.warn('Cognito signOut warning:', e);
    }

    try {
      localStorage.removeItem('authUser');
    } catch {}

    if (setAuthUser) {
      setAuthUser(null);
    }

    navigate('/login-aws');
  };

  return (
    <>
      {payload && <ConnectAttributes payload={payload} />}

      <header id="gts-main-header" className="header">
        <div className="topbar">
          <div className="topbar-inner">
            <div className="topbar-left">
              <div className="topbar-socials">
                <a href="https://x.com/GTS_CX_AI">
                  <i className="fa-brands fa-twitter" />
                </a>
                <a href="https://www.facebook.com/p/Global-Technology-Solutions-GTS-100070807695327/">
                  <i className="fa-brands fa-facebook" />
                </a>
                <a href="https://www.linkedin.com/company/global-tek-solutions-llc/">
                  <i className="fa-brands fa-linkedin" />
                </a>
                <a href="https://www.instagram.com/bots_and_bytes">
                  <i className="fa-brands fa-instagram" />
                </a>
              </div>

              <div className="topbar-info">
                <ul>
                  <li>
                    <div className="topbar-icon">
                      <i className="fa-solid fa-envelope" />
                    </div>

                    <div className="topbar-text">
                      <a href="mailto:hello@gts.gov">hello@gts.gov</a>
                    </div>
                  </li>

                  <li>
                    <div className="topbar-icon">
                      <i className="fa-solid fa-phone" />
                    </div>

                    <div className="topbar-text">
                      <a href={phoneInfo.href}>{phoneInfo.display}</a>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="topbar-right">
              <ul>
                <li>
                  <CustomLink to="/services">Services</CustomLink>
                </li>
                <li>
                  <CustomLink to="/departments">Departments</CustomLink>
                </li>
                <li>
                  <CustomLink to="/contact">Contact Us</CustomLink>
                </li>
                <li>
                  <CustomLink to="/about">About Us</CustomLink>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="main-menu sticky-header">
          <div className="main-menu-inner">
            {/* LEFT NAV TREE */}
            <div className="main-menu-left">
              <div className="main-menu-logo">
                <CustomLink to="/">
                  <img src={Logo} alt="img-1" width={140} />
                </CustomLink>
              </div>

              <div className="navigation">
                <ul className="main-menu-list list-unstyled">
                  <li>
                    <CustomLink to="/">Home</CustomLink>
                  </li>

                  <li 
                    className="has-dropdown"
                    onMouseEnter={() => {
                      // Clear any pending close timeout
                      if (departmentsCloseTimeoutRef.current) {
                        clearTimeout(departmentsCloseTimeoutRef.current);
                        departmentsCloseTimeoutRef.current = null;
                      }
                      setDepartmentsDropdownOpen(true);
                    }}
                    onMouseLeave={() => {
                      // Check if we're within grace period after clicking a submenu item
                      const gracePeriod = 500; // 500ms grace period
                      const timeSinceSubmenuClick = submenuClickTimeRef.current 
                        ? Date.now() - submenuClickTimeRef.current 
                        : Infinity;
                      
                      // If within grace period, don't close (user might be moving mouse back)
                      if (timeSinceSubmenuClick < gracePeriod) {
                        return;
                      }
                      
                      // Add a small delay to prevent accidental closure
                      departmentsCloseTimeoutRef.current = setTimeout(() => {
                        setDepartmentsDropdownOpen(false);
                      }, 200);
                    }}
                  >
                    <CustomLink 
                      to="/departments"
                      onClick={(e) => {
                        // Prevent navigation when clicking Departments - just open dropdown on hover
                        e.preventDefault();
                      }}
                    >
                      Departments
                    </CustomLink>
                    <ul 
                      className="list-unstyled departments-dropdown"
                      onMouseEnter={() => {
                        // Clear any pending close timeout
                        if (departmentsCloseTimeoutRef.current) {
                          clearTimeout(departmentsCloseTimeoutRef.current);
                          departmentsCloseTimeoutRef.current = null;
                        }
                        setDepartmentsDropdownOpen(true);
                      }}
                      onMouseLeave={() => {
                        // Check if we're within grace period after clicking a submenu item
                        const gracePeriod = 500; // 500ms grace period
                        const timeSinceSubmenuClick = submenuClickTimeRef.current 
                          ? Date.now() - submenuClickTimeRef.current 
                          : Infinity;
                        
                        // If within grace period, don't close (user might be moving mouse back)
                        if (timeSinceSubmenuClick < gracePeriod) {
                          return;
                        }
                        
                        // Add a small delay to prevent accidental closure
                        departmentsCloseTimeoutRef.current = setTimeout(() => {
                          setDepartmentsDropdownOpen(false);
                        }, 200);
                      }}
                      style={departmentsDropdownOpen ? {
                        display: 'block',
                        opacity: '1',
                        visibility: 'visible',
                        transform: 'translateY(0)'
                      } : {}}
                    >
                      {activeSubmenu === null ? (
                        <>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/dmv"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                // Prevent any nested dropdowns from showing on hover
                                e.stopPropagation();
                                // Set arrow background to white when link is hovered
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                // Reset arrow background when leaving link (unless arrow itself is hovered)
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                // If clicking on the arrow icon, prevent navigation
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('dmv');
                                }
                              }}
                            >
                              <span>Motor Vehicles</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('dmv');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/health"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('health');
                                }
                              }}
                            >
                              <span>Health &amp; Human Services</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('health');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/education"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('education');
                                }
                              }}
                            >
                              <span>Education</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('education');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/employment"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('employment');
                                }
                              }}
                            >
                              <span>Employment &amp; Workforce Development</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('employment');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/housing"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('housing');
                                }
                              }}
                            >
                              <span>Housing &amp; Community Development</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('housing');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/family-services"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  if (showSnapSubmenu) {
                                    setShowSnapSubmenu(false);
                                  }
                                  setActiveSubmenu('family-services');
                                }
                              }}
                            >
                              <span>Family &amp; Children Services</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  if (showSnapSubmenu) {
                                    setShowSnapSubmenu(false);
                                  }
                                  setActiveSubmenu('family-services');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/public-safety"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('public-safety');
                                }
                              }}
                            >
                              <span>Public Safety</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('public-safety');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/senior-services"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('senior-services');
                                }
                              }}
                            >
                              <span>Senior Services</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('senior-services');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/veterans"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('veterans');
                                }
                              }}
                            >
                              <span>Veterans Affairs</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('veterans');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/emergency"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('emergency');
                                }
                              }}
                            >
                              <span>Emergency Management</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('emergency');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                          <li style={{ position: 'relative' }}>
                            <CustomLink 
                              to="/department/technology"
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                  arrow.style.backgroundColor = 'transparent';
                                }
                              }}
                              onClick={(e) => {
                                if (e.target.closest('.submenu-arrow')) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('technology');
                                }
                              }}
                            >
                              <span>Innovation &amp; Technology</span>
                              <span 
                                className="submenu-arrow"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  submenuClickTimeRef.current = Date.now();
                                  setActiveSubmenu('technology');
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e75934';
                                  e.currentTarget.querySelector('i').style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                  // Check if parent link is still hovered
                                  const link = e.currentTarget.closest('a');
                                  if (link && link.matches(':hover')) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.querySelector('i').style.color = '#333';
                                  }
                                }}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}
                              >
                                <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                              </span>
                            </CustomLink>
                          </li>
                        </>
                      ) : activeSubmenu === 'dmv' ? (
                        <>
                          <li>
                            <CustomLink 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubmenu(null);
                              }}
                              className="back-button"
                              style={{ fontWeight: '600', color: '#e75934' }}
                            >
                              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                              Back to Departments
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/dmv/license-renewal">
                              Driver's License Renewal
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/dmv/vehicle-registration">
                              Vehicle Registration &amp; Title Services
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/dmv/driving-tests">
                              Driving Tests &amp; Learner's Permits
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/dmv/id-cards">
                              State Identification Cards &amp; Real ID
                            </CustomLink>
                          </li>
                        </>
                      ) : activeSubmenu === 'health' ? (
                        <>
                          <li>
                            <CustomLink 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubmenu(null);
                              }}
                              className="back-button"
                              style={{ fontWeight: '600', color: '#e75934' }}
                            >
                              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                              Back to Departments
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/health/initiatives">
                              Public Health Initiatives
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/health/mental-health-services">
                              Mental Health &amp; Substance Abuse
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/health/family-community-health">
                              Family &amp; Community Health
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/health/emergency-health-response">
                              Emergency Health Response &amp; Preparedness
                            </CustomLink>
                          </li>
                        </>
                      ) : activeSubmenu === 'education' ? (
                        <>
                          <li>
                            <CustomLink 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubmenu(null);
                              }}
                              className="back-button"
                              style={{ fontWeight: '600', color: '#e75934' }}
                            >
                              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                              Back to Departments
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/education/gtsu">
                              GTS State University
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/education/youth-mentorship">
                              Youth Development &amp; Mentorship
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/education/after-school-programs">
                              After-School &amp; Summer
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/education/special-education-support">
                              Special Education &amp; Learning Support
                            </CustomLink>
                          </li>
                        </>
                      ) : activeSubmenu === 'employment' ? (
                        <>
                          <li>
                            <CustomLink 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubmenu(null);
                              }}
                              className="back-button"
                              style={{ fontWeight: '600', color: '#e75934' }}
                            >
                              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                              Back to Departments
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/employment/job-training">
                              Job Training &amp; Skills Development
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/employment/youth-employment">
                              Youth &amp; Student Employment
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/employment/career-counseling">
                              Career Counseling &amp; Job Placement
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/employment/employer-partnerships">
                              Employer Partnerships &amp; Workforce Resources
                            </CustomLink>
                          </li>
                        </>
                      ) : activeSubmenu === 'housing' ? (
                        <>
                          <li>
                            <CustomLink 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubmenu(null);
                              }}
                              className="back-button"
                              style={{ fontWeight: '600', color: '#e75934' }}
                            >
                              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                              Back to Departments
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/housing/affordable-housing-programs">
                              Affordable Housing
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/housing/community-development">
                              Community Development &amp; Revitalization
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/housing/tenant-rights">
                              Tenant Rights &amp; Rental Assistance
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/housing/zoning-permits">
                              Zoning &amp; Permits
                            </CustomLink>
                          </li>
                        </>
                      ) : activeSubmenu === 'family-services' ? (
                        <>
                          {showSnapSubmenu ? (
                            <>
                              <li>
                                <CustomLink 
                                  to="#" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setShowSnapSubmenu(false);
                                  }}
                                  className="back-button"
                                  style={{ fontWeight: '600', color: '#e75934' }}
                                >
                                  <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                                  Back to Family &amp; Children Services
                                </CustomLink>
                              </li>
                              <li>
                                <CustomLink to="/department/family-services/snap/snap-eligibility">
                                  SNAP Eligibility
                                </CustomLink>
                              </li>
                              <li>
                                <CustomLink to="/department/family-services/snap/ebt">
                                  Electronic Benefits Transfer (EBT)
                                </CustomLink>
                              </li>
                              <li>
                                <CustomLink to="/department/family-services/snap/abawd">
                                  Work Requirements (ABAWD)
                                </CustomLink>
                              </li>
                            </>
                          ) : (
                            <>
                              <li>
                                <CustomLink 
                                  to="#" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setActiveSubmenu(null);
                                  }}
                                  style={{ fontWeight: '600', color: '#e75934' }}
                                >
                                  <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                                  Back to Departments
                                </CustomLink>
                              </li>
                              <li>
                                <CustomLink to="/department/family-services/family-support">
                                  Family Support &amp; Welfare Assistance
                                </CustomLink>
                              </li>
                              <li>
                                <CustomLink to="/department/family-services/child-youth-development">
                                  Child &amp; Youth Development
                                </CustomLink>
                              </li>
                              <li>
                                <CustomLink to="/department/family-services/domestic-violence-services">
                                  Domestic Violence &amp; Crisis Intervention
                                </CustomLink>
                              </li>
                              <li>
                                <CustomLink to="/department/family-services/community-engagement">
                                  Community Engagement &amp; Resource Centers
                                </CustomLink>
                              </li>
                              <li style={{ position: 'relative' }}>
                                <CustomLink 
                                  to="/department/family-services/snap"
                                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                                  onMouseEnter={(e) => {
                                    e.stopPropagation();
                                    const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                    if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                      arrow.style.backgroundColor = '#fff';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    const arrow = e.currentTarget.querySelector('.submenu-arrow');
                                    if (arrow && arrow.style.backgroundColor !== '#e75934') {
                                      arrow.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                  onClick={(e) => {
                                    if (e.target.closest('.submenu-arrow')) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      submenuClickTimeRef.current = Date.now();
                                      setShowSnapSubmenu(true);
                                    }
                                  }}
                                >
                                  <span>Supplemental Nutrition Assistance Program (SNAP)</span>
                                  <span 
                                    className="submenu-arrow"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      submenuClickTimeRef.current = Date.now();
                                      setShowSnapSubmenu(true);
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#e75934';
                                      e.currentTarget.querySelector('i').style.color = '#fff';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.querySelector('i').style.color = '#333';
                                    }}
                                    style={{ 
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      padding: '8px 12px',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s ease',
                                      marginLeft: '8px',
                                      flexShrink: 0
                                    }}
                                  >
                                    <i className="fa-solid fa-chevron-right" style={{ color: '#333', fontSize: '12px', transition: 'color 0.2s ease' }} />
                                  </span>
                                </CustomLink>
                              </li>
                            </>
                          )}
                        </>
                      ) : activeSubmenu === 'public-safety' ? (
                        <>
                          <li>
                            <CustomLink 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubmenu(null);
                              }}
                              className="back-button"
                              style={{ fontWeight: '600', color: '#e75934' }}
                            >
                              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                              Back to Departments
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/public-safety/community-policing">
                              Community Policing &amp; Crime Prevention
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/public-safety/fire-rescue">
                              Fire &amp; Rescue
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/public-safety/traffic-safety">
                              Traffic &amp; Road Safety Enforcement
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/public-safety/disaster-response">
                              Disaster Response &amp; Recovery
                            </CustomLink>
                          </li>
                        </>
                      ) : activeSubmenu === 'senior-services' ? (
                        <>
                          <li>
                            <CustomLink 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubmenu(null);
                              }}
                              className="back-button"
                              style={{ fontWeight: '600', color: '#e75934' }}
                            >
                              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                              Back to Departments
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/senior-services/health-wellness">
                              Senior Health &amp; Wellness
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/senior-services/social-activities">
                              Social &amp; Recreational Activities for Seniors
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/senior-services/elder-care">
                              Elder Care Support &amp; Resources
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/senior-services/housing">
                              Senior Housing &amp; Independent Living
                            </CustomLink>
                          </li>
                        </>
                      ) : activeSubmenu === 'veterans' ? (
                        <>
                          <li>
                            <CustomLink 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubmenu(null);
                              }}
                              className="back-button"
                              style={{ fontWeight: '600', color: '#e75934' }}
                            >
                              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                              Back to Departments
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/veterans/health-wellness">
                              Veterans' Health &amp; Wellness
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/veterans/job-training">
                              Employment &amp; Job Training Programs for Veterans
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/veterans/housing-support">
                              Veteran Housing Assistance &amp; Homelessness Prevention
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/veterans/benefits-claims">
                              Veterans' Benefits &amp; Claims
                            </CustomLink>
                          </li>
                        </>
                      ) : activeSubmenu === 'emergency' ? (
                        <>
                          <li>
                            <CustomLink 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubmenu(null);
                              }}
                              className="back-button"
                              style={{ fontWeight: '600', color: '#e75934' }}
                            >
                              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                              Back to Departments
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/emergency/disaster-preparedness">
                              Disaster Preparedness
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/emergency/emergency-response">
                              Emergency Response &amp; Recovery Operations
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/emergency/public-alerts">
                              Public Alerts &amp; Warning Systems
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/emergency/community-resilience">
                              Community Resilience &amp; Recovery
                            </CustomLink>
                          </li>
                        </>
                      ) : activeSubmenu === 'technology' ? (
                        <>
                          <li>
                            <CustomLink 
                              to="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubmenu(null);
                              }}
                              className="back-button"
                              style={{ fontWeight: '600', color: '#e75934' }}
                            >
                              <i className="fa-solid fa-arrow-left" style={{ marginRight: '8px' }} />
                              Back to Departments
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/technology/digital-transformation">
                              Digital Transformation &amp; Smart City Projects
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/technology/it-support">
                              Public Sector IT Support &amp; Solutions
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/technology/data-security">
                              Data Security &amp; Privacy Management
                            </CustomLink>
                          </li>
                          <li>
                            <CustomLink to="/department/technology/citizen-services">
                              Citizen-Focused Digital Services
                            </CustomLink>
                          </li>
                        </>
                      ) : null}
                    </ul>
                  </li>

                  <li>
                    <CustomLink to="/forms">Forms</CustomLink>
                  </li>
                </ul>
              </div>
            </div>

            {/* RIGHT HEADER BLOCK */}
            <div
              className="main-menu-right"
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '24px',
                flexWrap: 'nowrap',
                paddingRight: '16px',
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'visible',
                position: 'relative',
                zIndex: 100
              }}
            >
              {/* burger / mobile nav toggle */}
              <div
                className="mobile-menu-button mobile-nav-toggler"
                onClick={() => {
                  setBtnshow(true);
                }}
                style={{ flexShrink: 0 }}
              >
                <span />
                <span />
                <span />
              </div>

              {/* Welcome + ID only if logged in */}
              {authUser && payload && (
                <div
                  className="welcome-id"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    gap: '24px',
                    flexWrap: 'wrap'
                  }}
                >
                  <span>
                    Welcome <strong>{userData?.fullName || payload?.given_name || payload?.['cognito:username'] || 'User'}</strong>
                  </span>
                  <span>
                    ID <strong>{userData?.userId || userData?.customerID || payload?.['custom:User_ID'] || 'N/A'}</strong>
                  </span>
                </div>
              )}

              {/* Profile dropdown always visible */}
              <div
                className="navigation"
                style={{
                  display: 'flex',
                  alignItems: 'baseline'
                }}
              >
                <ul
                  className="main-menu-list list-unstyled"
                  style={{ 
                    display: 'flex', 
                    margin: 0, 
                    padding: 0,
                    flexShrink: 0,
                    position: 'relative'
                  }}
                >
                  <li className="has-dropdown">
                    <CustomLink 
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                    >
                      Profile
                    </CustomLink>

                    {/* submenu:
                       logged in: Edit + Logout
                       logged out: Log in
                       Uses same CSS classes as other dropdowns
                    */}
                    <ul className="list-unstyled">
                      {authUser && payload ? (
                        <>
                          <li>
                            <CustomLink to="/profile">Edit</CustomLink>
                          </li>
                          <li>
                            <CustomLink
                              to="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleLogout();
                              }}
                            >
                              Logout
                            </CustomLink>
                          </li>
                        </>
                      ) : (
                        <li>
                          <CustomLink to="/login-aws">Log in</CustomLink>
                        </li>
                      )}
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </header>

      {Btnshow && (
        <div className="mobile-nav-wrapper expanded">
          <div className="mobile-nav-overlay mobile-nav-toggler" />
          <div className="mobile-nav-content">
            <a
              href="#"
              className="mobile-nav-close mobile-nav-toggler"
              onClick={() => setBtnshow(false)}
            >
              <span />
              <span />
            </a>

            <div className="logo-box">
              <CustomLink to="/">
                <img src={Logo} alt="img-1" width={160} height={40} />
              </CustomLink>
            </div>

            <div className="mobile-nav-container">
              <ul className="main-menu-list list-unstyled">
                <li className="active has-dropdown">
                  <CustomLink to="#">
                    Home
                    <button
                      className={Home ? 'expanded' : ''}
                      onClick={() => {
                        setHome(!Home);
                      }}
                    >
                      <i className="fa-solid fa-chevron-right" />
                    </button>
                  </CustomLink>

                  {Home && (
                    <ul className="list-unstyled" style={{ display: 'block' }}>
                      <li><CustomLink to="/">Home 1</CustomLink></li>
                      <li><CustomLink to="/hometwo">Home 2</CustomLink></li>
                    </ul>
                  )}
                </li>

                <li className="has-dropdown">
                  <CustomLink to="#">
                    Pages
                    <button className={Pages ? 'expanded' : ''}>
                      <i
                        className="fa-solid fa-chevron-right"
                        onClick={() => {
                          setPages(!Pages);
                        }}
                      />
                    </button>
                  </CustomLink>
                  {Pages && (
                    <ul className="list-unstyled" style={{ display: 'block' }}>
                      <li><CustomLink to="/about">About</CustomLink></li>
                      <li><CustomLink to="/team">Team</CustomLink></li>
                      <li><CustomLink to="/teamdetails">Team Details</CustomLink></li>
                      <li><CustomLink to="/portfolio">Portfolio</CustomLink></li>
                      <li><CustomLink to="/portfoliodetails">Portfolio Details</CustomLink></li>
                      <li><CustomLink to="/causes">Causes</CustomLink></li>
                      <li><CustomLink to="/causedetails">Cause Details</CustomLink></li>
                    </ul>
                  )}
                </li>

                <li className="has-dropdown">
                  <CustomLink to="#">
                    Services
                    <button className={Services ? 'expanded' : ''}>
                      <i
                        className="fa-solid fa-chevron-right"
                        onClick={() => {
                          setServices(!Services);
                        }}
                      />
                    </button>
                  </CustomLink>
                  {Services && (
                    <ul className="list-unstyled" style={{ display: 'block' }}>
                      <li><CustomLink to="/services">Services</CustomLink></li>
                      <li><CustomLink to="/servicedetails">Service Details</CustomLink></li>
                    </ul>
                  )}
                </li>

                <li className="has-dropdown">
                  <CustomLink to="#">
                    Departments
                    <button className={Departments ? 'expanded' : ''}>
                      <i
                        className="fa-solid fa-chevron-right"
                        onClick={() => {
                          setDepartments(!Departments);
                        }}
                      />
                    </button>
                  </CustomLink>
                  {Departments && (
                    <ul className="list-unstyled" style={{ display: 'block' }}>
                      <li><CustomLink to="/departments">All Departments</CustomLink></li>
                      <li><CustomLink to="/department/dmv">Motor Vehicles</CustomLink></li>
                      <li><CustomLink to="/department/health">Health & Human Services</CustomLink></li>
                      <li><CustomLink to="/department/education">Education</CustomLink></li>
                      <li><CustomLink to="/department/employment">Employment & Workforce Development</CustomLink></li>
                      <li><CustomLink to="/department/housing">Housing & Community Development</CustomLink></li>
                      <li><CustomLink to="/department/family-services">Family & Children Services</CustomLink></li>
                      <li><CustomLink to="/department/public-safety">Public Safety</CustomLink></li>
                      <li><CustomLink to="/department/senior-services">Senior Services</CustomLink></li>
                      <li><CustomLink to="/department/veterans">Veterans Affairs</CustomLink></li>
                      <li><CustomLink to="/department/emergency">Emergency Management</CustomLink></li>
                      <li><CustomLink to="/department/technology">Innovation & Technology</CustomLink></li>
                    </ul>
                  )}
                </li>

                <li className="has-dropdown">
                  <CustomLink to="#">
                    Events
                    <button className={Events ? 'expanded' : ''}>
                      <i
                        className="fa-solid fa-chevron-right"
                        onClick={() => {
                          setEvents(!Events);
                        }}
                      />
                    </button>
                  </CustomLink>
                  {Events && (
                    <ul className="list-unstyled" style={{ display: 'block' }}>
                      <li><CustomLink to="/events">Events</CustomLink></li>
                      <li><CustomLink to="/eventdetails">Event Details</CustomLink></li>
                    </ul>
                  )}
                </li>

                <li className="has-dropdown">
                  <CustomLink to="#">
                    News
                    <button className={News ? 'expanded' : ''}>
                      <i
                        className="fa-solid fa-chevron-right"
                        onClick={() => {
                          setNews(!News);
                        }}
                      />
                    </button>
                  </CustomLink>
                  {News && (
                    <ul className="list-unstyled" style={{ display: 'block' }}>
                      <li><CustomLink to="/news">News</CustomLink></li>
                      <li><CustomLink to="/newsdetails">News Details</CustomLink></li>
                    </ul>
                  )}
                </li>

                <li>
                  <CustomLink to="/forms">Forms</CustomLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Render bot-specific component with user data */}
      {/* Use a stable key based on activeBotId to prevent unnecessary remounts on route changes */}
      {/* CRITICAL: Use a stable key that doesn't change on route changes to prevent component unmounting */}
      {BotComponent && <BotComponent key={`bot-${activeBotId}`} userData={userData} payload={payload} />}
    </>
  );
}

export default SharedHeader;
